import { addCalendarDays } from './date-only';
import type { ComplianceDeadlineItem } from '../compliance-deadlines/types';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const REGISTER_LABELS: Record<string, string> = {
  PROVIDER_DUTIES: 'Provider duties and reminders',
  CHSP_REPORTING: 'CHSP reporting',
  COMPLAINT: 'Complaints',
  SIRS_NOTIFICATION: 'Serious incident notification',
  RN_24_7_MONTHLY_REPORT: '24/7 RN monthly report',
  RECURRING_ORGANISATIONAL_OBLIGATION: 'Organisation-wide statutory obligation',
};

export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/** Folds RFC 5545 content at 75 UTF-8 octets, never inside a code point. */
export function foldIcsLine(line: string): string {
  const segments: string[] = [];
  let current = '';
  let octets = 0;
  let continuation = false;
  for (const character of line) {
    const size = Buffer.byteLength(character, 'utf8');
    if (octets + size > (continuation ? 74 : 75) && current) {
      segments.push(`${continuation ? ' ' : ''}${current}`);
      current = character;
      octets = size;
      continuation = true;
    } else {
      current += character;
      octets += size;
    }
  }
  segments.push(`${continuation ? ' ' : ''}${current}`);
  return segments.join('\r\n');
}

function utcIcsTimestamp(value: Date): string {
  return value
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

/**
 * SHA-256 (hex) supplied by the caller. This library is exported from the package's main
 * entry, which browser bundles also load, so it must not import Node's crypto module
 * itself; the server route passes Node's implementation in.
 */
export type Sha256Hex = (value: string) => string;

/** Opaque UIDs use per-feed salt, never references or database identifiers. */
export function complianceCalendarUid(
  sha256Hex: Sha256Hex,
  feedSalt: string,
  itemKey: string,
): string {
  return sha256Hex(`${feedSalt}:${itemKey}`);
}

/** Privacy boundary: title, reference, record URL and all metadata are excluded. */
export function serializeComplianceCalendarIcs(
  items: ComplianceDeadlineItem[],
  input: { feedSalt: string; sha256Hex: Sha256Hex; now: Date; appUrl: string },
): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'PRODID:-//Statura Care//Compliance Calendar//EN',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'X-WR-CALNAME:Statura Compliance due dates',
  ];
  for (const item of items) {
    const summary =
      item.kind === 'STATUTORY' ? 'Compliance obligation due' : 'Compliance target due';
    lines.push(
      'BEGIN:VEVENT',
      `UID:${complianceCalendarUid(input.sha256Hex, input.feedSalt, item.id)}`,
      `DTSTAMP:${utcIcsTimestamp(input.now)}`,
      `SUMMARY:${summary}`,
    );
    if (DATE_ONLY.test(item.dueAt)) {
      // RFC 5545: an all-day event's DTEND is exclusive, i.e. the following day.
      lines.push(
        `DTSTART;VALUE=DATE:${item.dueAt.replace(/-/g, '')}`,
        `DTEND;VALUE=DATE:${addCalendarDays(item.dueAt, 1).replace(/-/g, '')}`,
      );
    } else {
      // A deadline instant is a point in time: DTSTART alone, no zero-length DTEND.
      lines.push(`DTSTART:${utcIcsTimestamp(new Date(item.dueAt))}`);
    }
    const description = `${REGISTER_LABELS[item.register] ?? 'Compliance item'}\n${
      input.appUrl
    }/compliance/work`;
    lines.push(`DESCRIPTION:${escapeIcsText(description)}`, 'END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return `${lines.map(foldIcsLine).join('\r\n')}\r\n`;
}
